import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    AlertDescription,
    Button,
    Combobox,
    Form,
    FormControl,
    FormField,
    FormLabel,
    FormMessage,
    Input,
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    Textarea,
} from '@onward-delivery/design';

import { draftFromAutomation, emptyDraft, toAutomationInput, validateDraft } from './utils';

const URL_SUGGESTIONS_ID = 'automation-url-suggestions';

const TIMEZONES = (Intl.supportedValuesOf?.('timeZone') || []).map((zone) => ({ value: zone, label: zone }));

const Field = ({ name, label, error, hint, children }) => (
    <FormField name={name} error={error}>
        <FormLabel>{label}</FormLabel>
        <FormControl>{children}</FormControl>
        {error ? <FormMessage>{error}</FormMessage> : hint && <p className="text-xs text-muted">{hint}</p>}
    </FormField>
);

const AutomationSheet = ({ open, automation, onOpenChange, onSave, saving, clients, urlSuggestions }) => {
    const [draft, setDraft] = useState(emptyDraft);
    const [submitted, setSubmitted] = useState(false);
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setDraft(automation ? draftFromAutomation(automation) : emptyDraft());
        setSubmitted(false);
        setSaveError(null);
    }, [open, automation]);

    const errors = useMemo(() => (submitted ? validateDraft(draft) : {}), [submitted, draft]);

    const timezones = useMemo(
        () =>
            !draft.timezone || TIMEZONES.some((zone) => zone.value === draft.timezone)
                ? TIMEZONES
                : [{ value: draft.timezone, label: draft.timezone }, ...TIMEZONES],
        [draft.timezone]
    );

    const set = (field) => (value) => setDraft((prev) => ({ ...prev, [field]: value }));
    const setFromEvent = (field) => (event) => set(field)(event.target.value);

    const handleSubmit = async () => {
        setSubmitted(true);
        if (Object.keys(validateDraft(draft)).length) return;

        setSaveError(null);
        try {
            await onSave(automation?.automation_id, toAutomationInput(draft));
            onOpenChange(false);
        } catch (e) {
            setSaveError(e.message);
        }
    };

    const clientField = (field, label, options) => (
        <Field name={field} label={label}>
            <Combobox
                options={options}
                value={draft[field]}
                onValueChange={set(field)}
                placeholder="None"
                searchPlaceholder="Search clients…"
            />
        </Field>
    );

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-full max-w-xl overflow-y-auto pb-24">
                <SheetTitle className="text-lg font-semibold">
                    {automation ? 'Edit automation' : 'New automation'}
                </SheetTitle>
                <SheetDescription className="text-sm text-muted">
                    Saving syncs the Cloud Scheduler job for this automation.
                </SheetDescription>

                <Form className="mt-4 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
                    <Field
                        name="url"
                        label="Endpoint"
                        error={errors.url}
                        hint="Path after the functions URL, e.g. v2-lib-automations-endpoints/pod-recovery/batch"
                    >
                        <Input
                            className="!font-mono"
                            list={URL_SUGGESTIONS_ID}
                            value={draft.url}
                            onChange={setFromEvent('url')}
                            error={!!errors.url}
                        />
                    </Field>
                    <datalist id={URL_SUGGESTIONS_ID}>
                        {urlSuggestions.map((url) => (
                            <option key={url} value={url} />
                        ))}
                    </datalist>

                    <div className="grid grid-cols-2 gap-4">
                        <Field
                            name="crontab"
                            label="Crontab"
                            error={errors.crontab}
                            hint="minute hour day-of-month month day-of-week"
                        >
                            <Input
                                className="!font-mono"
                                placeholder="0 20 * * *"
                                value={draft.crontab}
                                onChange={setFromEvent('crontab')}
                                error={!!errors.crontab}
                            />
                        </Field>
                        <Field name="timezone" label="Timezone" error={errors.timezone}>
                            <Combobox
                                options={timezones}
                                value={draft.timezone}
                                onValueChange={set('timezone')}
                                placeholder="Select a timezone"
                                searchPlaceholder="Search timezones…"
                            />
                        </Field>
                    </div>

                    {clientField('shipper_id', 'Shipper', clients.shippers)}
                    {clientField('carrier_id', 'Carrier', clients.carriers)}
                    {clientField('middle_mile_carrier_id', 'Middle Mile Carrier', clients.carriers)}

                    <Field name="params" label="Params (JSON)" error={errors.params}>
                        <Textarea
                            className="!font-mono text-sm"
                            rows={8}
                            spellCheck={false}
                            value={draft.params}
                            onChange={setFromEvent('params')}
                            error={!!errors.params}
                        />
                    </Field>
                    <Field
                        name="body"
                        label="Body (JSON)"
                        error={errors.body}
                        hint="Merged into the scheduler request body alongside the automation row"
                    >
                        <Textarea
                            className="!font-mono text-sm"
                            rows={6}
                            spellCheck={false}
                            value={draft.body}
                            onChange={setFromEvent('body')}
                            error={!!errors.body}
                        />
                    </Field>

                    {saveError && (
                        <Alert variant="destructive">
                            <AlertDescription>{saveError}</AlertDescription>
                        </Alert>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={saving}>
                            Save
                        </Button>
                    </div>
                </Form>
            </SheetContent>
        </Sheet>
    );
};

export default AutomationSheet;
